// Каталог Библиотек: один запрос всего списка (≈70 позиций), фильтры и поиск —
// на клиенте; счётчики вкладок — из ответа сервера (до фильтров).

import { create } from 'zustand'
import { ApiError, describe, http } from '../custom/http'
import type { LibraryItem, LibraryResponse, LibType } from './types'

export type Tab = 'all' | LibType

interface LibState {
  items: LibraryItem[]
  counts: LibraryResponse['counts'] | null
  loaded: boolean
  error: string | null
  tab: Tab
  installedOnly: boolean
  q: string
  /** id → идёт установка */
  busy: Record<string, boolean>
  /** id → ошибка установки */
  errors: Record<string, { text: string; conflict: boolean }>
  load: () => Promise<void>
  setTab: (tab: Tab, installedOnly?: boolean) => void
  setQ: (q: string) => void
  install: (id: string, force?: boolean) => Promise<void>
}

export const useLibrary = create<LibState>()((set, get) => ({
  items: [],
  counts: null,
  loaded: false,
  error: null,
  tab: 'all',
  installedOnly: false,
  q: '',
  busy: {},
  errors: {},
  load: async () => {
    try {
      const res = await http<LibraryResponse>('/library')
      set({ items: res.items, counts: res.counts, loaded: true, error: null })
    } catch (error) {
      set({ loaded: true, error: describe(error) })
    }
  },
  setTab: (tab, installedOnly = false) => set({ tab, installedOnly }),
  setQ: (q) => set({ q }),
  install: async (id, force = false) => {
    set((s) => ({ busy: { ...s.busy, [id]: true }, errors: { ...s.errors, [id]: undefined as never } }))
    try {
      await http(`/library/install`, { method: 'POST', body: JSON.stringify({ id, force }) }, 120_000)
      set((s) => ({ items: s.items.map((it) => (it.id === id ? { ...it, installed: true } : it)) }))
      void get().load()
    } catch (error) {
      const status = error instanceof ApiError ? error.status : 0
      const text = status === 403
        ? 'ставить может только владелец сервера: ~/.claude общий для всех профилей'
        : status === 409
          ? 'уже есть на сервере'
          : status === 502
            ? `источник не ответил: ${describe(error)}`
            : describe(error)
      set((s) => ({ errors: { ...s.errors, [id]: { text, conflict: status === 409 } } }))
    } finally {
      set((s) => ({ busy: { ...s.busy, [id]: false } }))
    }
  },
}))

export function visible(items: LibraryItem[], tab: Tab, installedOnly: boolean, q: string): LibraryItem[] {
  const needle = q.trim().toLowerCase()
  return items.filter((it) => {
    if (tab !== 'all' && it.type !== tab) return false
    if (installedOnly && !it.installed) return false
    if (!needle) return true
    return [it.name, it.title, it.description, it.source, it.author ?? ''].some((f) => f.toLowerCase().includes(needle))
  })
}
